import React, { useEffect, useRef, useState } from 'react';

interface LazyBackgroundImageProps {
  /**
   * WebP image source (preferred format)
   */
  webpSrc: string;
  /**
   * Fallback image source (PNG/JPG for older browsers)
   */
  fallbackSrc: string;
  /**
   * CSS class names to apply
   */
  className?: string;
  /**
   * Inline styles to apply
   */
  style?: React.CSSProperties;
  /**
   * Children to render inside the container
   */
  children?: React.ReactNode;
  /**
   * Root margin for intersection observer (default: "100px")
   * Positive values will trigger loading before element enters viewport
   */
  rootMargin?: string;
  /**
   * Blur-up placeholder color (default: transparent)
   */
  placeholderColor?: string;
}

/**
 * LazyBackgroundImage component that uses Intersection Observer
 * to lazy load background images with WebP support and fallback.
 *
 * Features:
 * - Lazy loading via Intersection Observer
 * - WebP format with PNG/JPG fallback
 * - Smooth fade-in transition
 * - Optional blur-up placeholder
 *
 * Usage:
 * ```tsx
 * <LazyBackgroundImage
 *   webpSrc="/images/optimized/flowers.webp"
 *   fallbackSrc="/images/flowers.png"
 *   className="absolute inset-0 bg-cover bg-no-repeat"
 * >
 *   <div>Content here</div>
 * </LazyBackgroundImage>
 * ```
 */
export const LazyBackgroundImage: React.FC<LazyBackgroundImageProps> = ({
  webpSrc,
  fallbackSrc,
  className = '',
  style = {},
  children,
  rootMargin = '100px',
  placeholderColor = 'transparent',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');

  // Check WebP support
  useEffect(() => {
    const checkWebPSupport = async () => {
      // Check if browser supports WebP
      const webpSupported = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width === 1);
        img.onerror = () => resolve(false);
        img.src =
          'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
      });

      setImageSrc(webpSupported ? webpSrc : fallbackSrc);
    };

    checkWebPSupport();
  }, [webpSrc, fallbackSrc]);

  // Set up Intersection Observer
  useEffect(() => {
    if (!ref.current || !imageSrc) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, imageSrc]);

  // Load the image once it's in view
  useEffect(() => {
    if (!isInView || !imageSrc) return;

    const img = new Image();
    img.onload = () => {
      setIsLoaded(true);
    };
    img.src = imageSrc;
  }, [isInView, imageSrc]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        backgroundColor: !isLoaded ? placeholderColor : 'transparent',
        backgroundImage: isLoaded ? `url(${imageSrc})` : 'none',
        transition: 'opacity 0.5s ease-in-out',
        opacity: isLoaded ? 1 : 0,
      }}
    >
      {children}
    </div>
  );
};

export default LazyBackgroundImage;
